// debug-help.ts — the plain-language help behind every ⓘ in the player's debug
// mode: what the element means and what it is for. Text only; no logic.

import type { FindingKind } from './sync-findings.util';

export const FINDING_HELP: Record<FindingKind, string> = {
  untranscribed: 'Someone spoke (there is audio on the Human row) but no transcript turn covers it. '
    + 'Their words were lost. Play the span to confirm, then read the inspector for why.',
  gated_onset: 'The speech began while the AI was audible, so the host mic\'s echo gate replaced the quiet '
    + 'onset with silence and the model never heard it. Explains "they talked but nothing printed".',
  talked_over: 'A human started speaking and the AI kept talking for more than 1.5 s with no honored '
    + 'interrupt. Explains "the AI would not stop".',
  phantom: 'A transcript turn with no speech under it, usually the AI\'s own voice coming back through a '
    + 'speaker and being transcribed as the human.',
  late: 'The text exists but arrived more than 10 s after the speech ended. Not missing, just slow.',
  guest_not_received: 'A guest spoke according to the timeline, but the recorded human leg is flat there: '
    + 'their audio never reached the host.',
};

export const DEBUG_HELP = {
  mode: 'Lines up three things on one clock: the audio that was heard (rows), the text that was written '
    + '(Transcript row) and the decisions the call made in between (Events row). Switch it on when speech '
    + 'seems missing from the transcript or the AI did not stop for someone.',
  strip: 'How many places the audio and the transcript disagree, by kind. Hover a chip for what the kind means.',
  gateClosed: 'Total time the echo gate was closed while the AI spoke. During those spans quiet mic audio '
    + 'was replaced with silence, so soft interruptions were never heard.',
  copyReport: 'Copies every finding with its evidence as plain text, ready for a ticket.',
  buildWaveform: 'This recording predates waveforms. Builds them now on the server from its audio files '
    + '(a few seconds) and saves them on the recording for next time.',
  rows: 'Bars: who was speaking, measured from the recorded audio. Waveform: that leg\'s loudness. '
    + 'Amber outline: the AI talked over someone. Red outline: speech with no transcript. Hatched red '
    + '(Human row): the echo gate was closed.',
  transcriptRow: 'One block per transcript turn, placed on the audio clock. Solid = matched to measured speech, '
    + 'dashed = estimated from its timestamp. Red or amber dashed blocks are findings; click one to inspect it.',
  eventsRow: 'What the call decided, on the same clock. Green tick = an interrupt was honored, red tick = it was '
    + 'discarded or held. Bars: gate closed (red), STT audio window (indigo), guest audio arriving (amber).',
  playSolo: 'Plays exactly this span with only that side audible, so you can hear whether the words were really there.',
  playBoth: 'The same span with both sides, the way the listener heard it.',
  loop: 'Repeat the span until you pause, for listening closely.',
  copyLine: 'Copies just this finding and its evidence as text.',
  inspector: 'Every signal we hold for the selected span, then the single most likely reason.',
  inspectorRows: {
    'Human leg': 'Loudness of the recorded human audio in this span. "Flat" means the audio never reached the recording, '
      + 'so the problem is transport (relay, guest link), not transcription.',
    'AI leg': 'Whether the AI was speaking during the span, and its turn window.',
    'Echo gate': 'The host mic\'s half-duplex gate. "Closed" means quiet mic chunks were replaced with silence because '
      + 'the AI was audible, so the model could not hear an onset.',
    'Interrupt': 'Barge-in attempts in the span and how each ended. flush or honored = the AI stopped; discarded or hold = it kept going.',
    'Gemini input text': 'Whether Gemini\'s own transcription of the audio returned any text for this span.',
    'Windowed STT': 'The backend speech-to-text used while the AI is paused: was audio sent for this span, and did text come back.',
    'Guest chunks': 'Whether a guest\'s or phone caller\'s audio was arriving at the host during the span.',
    'Verdict': 'The one mechanism the rows above point to.',
  } as Record<string, string>,
};
